import React, { useContext, useEffect, useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import { Spinner } from 'react-bootstrap';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import {
  formatOrderContactUpdatedAt,
  getOrderRequestDetail,
  getOrderContactSettings,
  patchOrderRequest,
} from '../../clients/order_request_client';
import { requestQuotesFromCatalog } from '../../clients/quote_client';
import { showToast } from '../../config_and_helpers/helpers';
import OrderDetailModal from '../Order/OrderDetailModal';
import GlobalOrderContactModal from './GlobalOrderContactModal';
import { logError, logInfo } from '../../config_and_helpers/logger';
import {
  catalogEmailSuccessMessage,
  mergeCatalogQuoteEmailResults,
} from '../../utils/catalogQuoteEmailResponse';
import {
  buildCatalogQuoteLinesFromOrderRequestLines,
  groupOrderRequestLinesBySupplier,
} from '../../utils/orderRequestCart';
import '../Glass/ProcureModalStyle.css';
import '../Glass/GlassStyle.css';

const LOG_CONTEXT = 'OrderRequestDetailModal';

/**
 * Match a supplier group to a row in merged catalog quote email results.
 *
 * @param {{ corporate_demand_ref?: string, suppliers?: Array }} result
 * @param {{ supplierId: number|null, supplierName: string }} group
 * @returns {{ supplier_name?: string, emailSent?: boolean, item_count?: number }|null}
 */
function findSupplierQuoteResultForGroup(result, group) {
  if (!result?.suppliers?.length) return null;
  return (
    result.suppliers.find(s => {
      if (
        group.supplierId != null &&
        s.supplier_id != null &&
        String(s.supplier_id) === String(group.supplierId)
      ) {
        return true;
      }
      return (
        (s.supplier_name || '').trim().toLowerCase() ===
        (group.supplierName || '').trim().toLowerCase()
      );
    }) ?? null
  );
}

/**
 * Inline summary after catalog quote-request emails are sent.
 *
 * @param {{ corporate_demand_ref?: string, suppliers?: Array }} result
 */
function CatalogQuoteEmailResultSummary({ result }) {
  if (!result?.suppliers?.length) return null;
  return (
    <Alert variant="success" className="mb-3">
      {result.corporate_demand_ref ? (
        <p className="mb-2">
          <strong>Corporate demand ref:</strong> {result.corporate_demand_ref}
        </p>
      ) : null}
      <ul className="mb-0 ps-3">
        {result.suppliers.map((s, idx) => (
          <li key={s.supplier_id ?? s.supplier_name ?? idx}>
            {s.supplier_name || 'Supplier'}
            {s.item_count != null ? ` — ${s.item_count} item(s)` : ''}
            {' — '}
            email {s.emailSent ? 'sent' : 'not sent'}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * Lines grouped by supplier with per-group quote actions.
 *
 * @param {Object} props
 * @param {Array} props.supplierGroups
 * @param {boolean} props.canRequestQuotes
 * @param {string} props.globalQuoteDisabledReason
 * @param {string|null} props.requestingQuoteScope
 * @param {{ corporate_demand_ref?: string, suppliers?: Array }|null} props.quoteSuccessResult
 * @param {function} props.onRequestQuotesForSupplier
 */
function OrderRequestLinesBySupplier({
  supplierGroups,
  canRequestQuotes,
  globalQuoteDisabledReason,
  requestingQuoteScope,
  quoteSuccessResult,
  onRequestQuotesForSupplier,
}) {
  const isQuoteInFlight = requestingQuoteScope != null;

  return (
    <div className="mb-3">
      {supplierGroups.map(group => {
        const quotableCount = buildCatalogQuoteLinesFromOrderRequestLines(
          group.lines
        ).length;
        const supplierResult = findSupplierQuoteResultForGroup(
          quoteSuccessResult,
          group
        );
        const groupDisabledReason =
          globalQuoteDisabledReason ||
          (quotableCount === 0
            ? 'No quotable lines in this supplier group.'
            : '');
        const isThisRequesting = requestingQuoteScope === group.supplierKey;

        return (
          <div
            key={group.supplierKey}
            className="mb-4 pb-3 border-bottom"
          >
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h6 className="mb-0">
                {group.supplierName}{' '}
                <span className="text-muted fw-normal">
                  ({group.lines.length} line
                  {group.lines.length === 1 ? '' : 's'})
                </span>
              </h6>
              {canRequestQuotes && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={
                    !!groupDisabledReason ||
                    isQuoteInFlight ||
                    isThisRequesting
                  }
                  title={groupDisabledReason || undefined}
                  onClick={() =>
                    onRequestQuotesForSupplier(group.supplierKey)
                  }
                >
                  {isThisRequesting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Sending…
                    </>
                  ) : (
                    'Request quotes'
                  )}
                </Button>
              )}
            </div>
            {supplierResult ? (
              <Alert
                variant={supplierResult.emailSent ? 'success' : 'warning'}
                className="py-2 mb-2"
              >
                Quote email{' '}
                {supplierResult.emailSent ? 'sent' : 'not sent'}
                {supplierResult.item_count != null
                  ? ` (${supplierResult.item_count} item(s))`
                  : ''}
              </Alert>
            ) : null}
            {quotableCount === 0 && group.lines.length > 0 ? (
              <p className="text-muted small mb-2">
                Lines in this group are not quotable (invalid product or
                quantity).
              </p>
            ) : null}
            <div className="glass-table-wrap">
            <Table striped bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>CAT#</th>
                  <th>Qty</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {group.lines.map(line => {
                  const isQuotable =
                    buildCatalogQuoteLinesFromOrderRequestLines([line])
                      .length > 0;
                  return (
                    <tr
                      key={line.id ?? `${line.product_id}-${line.quantity}`}
                    >
                      <td>
                        {line.product_name}
                        {!isQuotable ? (
                          <>
                            {' '}
                            <Badge bg="secondary" className="ms-1">
                              Not quotable
                            </Badge>
                          </>
                        ) : null}
                      </td>
                      <td>{line.cat_num || '—'}</td>
                      <td>{line.quantity}</td>
                      <td>{line.notes || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Detail modal for a procurement order request (fulfill / cancel / link order).
 *
 * @param {Object} props
 * @param {Object} [props.requestRow] - List row (optional prefetch).
 * @param {number|string} [props.requestId]
 * @param {function} [props.onUpdated] - Called after successful PATCH.
 */
const OrderRequestDetailModal = ({
  requestRow,
  requestId: requestIdProp,
  onUpdated,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const requestId = requestIdProp ?? requestRow?.id;
  const label = requestRow?.request_ref || `Request #${requestId}`;

  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [notes, setNotes] = useState('');
  const [linkedOrder, setLinkedOrder] = useState('');

  const [requestingQuoteScope, setRequestingQuoteScope] = useState(null);
  const [quoteSubmitErrors, setQuoteSubmitErrors] = useState([]);
  const [quoteSuccessResult, setQuoteSuccessResult] = useState(null);
  const [globalContactSettings, setGlobalContactSettings] = useState(null);
  const [isLoadingGlobalContact, setIsLoadingGlobalContact] = useState(false);
  const [globalContactLoadError, setGlobalContactLoadError] = useState('');
  const [showGlobalContactModal, setShowGlobalContactModal] = useState(false);

  const supplierGroups = useMemo(
    () => groupOrderRequestLinesBySupplier(detail?.lines),
    [detail?.lines]
  );

  const loadDetail = async () => {
    if (!requestId) return;
    setLoading(true);
    const response = await getOrderRequestDetail(token, requestId);
    setLoading(false);
    if (response?.success) {
      setDetail(response.data);
      setNotes(response.data?.notes ?? '');
      setLinkedOrder(
        response.data?.linked_order != null
          ? String(response.data.linked_order)
          : ''
      );
    } else {
      showToast(
        response?.messages?.[0] || 'Failed to load order request',
        'error',
        'top-center',
        4000
      );
    }
  };

  const loadGlobalContactSettings = async () => {
    if (!token) return;
    setIsLoadingGlobalContact(true);
    setGlobalContactLoadError('');
    const response = await getOrderContactSettings(token);
    setIsLoadingGlobalContact(false);
    if (response?.success) {
      setGlobalContactSettings(response.data);
    } else {
      setGlobalContactSettings(null);
      setGlobalContactLoadError(
        response?.messages?.[0] ||
          'Lab contact settings could not be loaded.'
      );
    }
  };

  const handleOpen = () => {
    setShow(true);
    setDetail(null);
    setQuoteSubmitErrors([]);
    setQuoteSuccessResult(null);
    setRequestingQuoteScope(null);
    loadDetail();
    loadGlobalContactSettings();
  };

  useEffect(() => {
    if (!show) {
      setQuoteSubmitErrors([]);
      setQuoteSuccessResult(null);
      setRequestingQuoteScope(null);
      setShowGlobalContactModal(false);
    }
  }, [show]);

  const handlePatch = async status => {
    if (!requestId) return;
    const payload = { status };
    if (notes.trim()) payload.notes = notes.trim();
    const linked = linkedOrder.trim();
    if (linked) {
      const orderId = Number(linked);
      if (!Number.isInteger(orderId) || orderId < 1) {
        showToast('Linked order must be a positive integer id', 'error');
        return;
      }
      payload.linked_order = orderId;
    }

    setPendingStatus(status);
    logInfo(LOG_CONTEXT, 'patch order request', { requestId, status });
    const response = await patchOrderRequest(token, requestId, payload);
    setPendingStatus(null);

    if (response?.success) {
      setDetail(response.data);
      showToast(`Order request ${response.data?.request_ref || ''} updated`, 'success');
      onUpdated?.(response.data);
      if (status === 'FULFILLED' || status === 'CANCELLED') {
        setShow(false);
      }
    } else {
      logError(LOG_CONTEXT, 'patch failed', {
        requestId,
        status,
        errorType: typeof response?.error,
        message: response?.messages?.[0] || 'Update failed',
      });
      showToast(
        response?.messages?.[0] || 'Update failed',
        'error',
        'top-center',
        4000
      );
    }
  };

  /**
   * Send catalog quote-request emails for one supplier group in this request.
   *
   * @param {{ supplierKey: string }} options
   */
  const handleRequestSupplierQuotes = async ({ supplierKey } = {}) => {
    if (!detail?.id || supplierKey == null) return;

    const group = supplierGroups.find(g => g.supplierKey === supplierKey);
    const sourceLines = group?.lines ?? [];
    const lines = buildCatalogQuoteLinesFromOrderRequestLines(sourceLines);
    if (lines.length === 0) {
      showToast(
        'This supplier group has no quotable lines.',
        'error',
        'top-center',
        4000,
      );
      return;
    }

    setRequestingQuoteScope(supplierKey);
    setQuoteSubmitErrors([]);

    const payload = {
      lines,
      order_request_id: Number(detail.id),
    };

    logInfo(LOG_CONTEXT, 'requestQuotesFromCatalog submit', {
      requestId: detail.id,
      supplierKey,
      lineCount: lines.length,
    });

    const response = await requestQuotesFromCatalog(token, payload);
    setRequestingQuoteScope(null);

    if (response?.success) {
      setQuoteSuccessResult(prev =>
        mergeCatalogQuoteEmailResults(prev, response.data),
      );
      const msg = catalogEmailSuccessMessage(response.data);
      showToast(msg, 'success', 'top-right', 5000);
      logInfo(LOG_CONTEXT, 'requestQuotesFromCatalog success', {
        requestId: detail.id,
        supplierKey,
        corporateDemandRef: response.data?.corporate_demand_ref,
        supplierCount: response.data?.suppliers?.length,
      });
    } else {
      logError(LOG_CONTEXT, 'requestQuotesFromCatalog failed', {
        requestId: detail.id,
        supplierKey,
        messages: response?.messages,
      });
      setQuoteSubmitErrors(
        response?.messages?.length
          ? response.messages
          : ['Request failed. Please try again.'],
      );
    }
  };

  const quoteLines = buildCatalogQuoteLinesFromOrderRequestLines(
    detail?.lines
  );
  const hasAllocatedGlobalContact =
    !!globalContactSettings?.contact_name &&
    !!globalContactSettings?.contact_email;

  let globalQuoteDisabledReason = '';
  if (isLoadingGlobalContact) {
    globalQuoteDisabledReason = 'Loading lab contact settings...';
  } else if (globalContactLoadError) {
    globalQuoteDisabledReason =
      'Quote requests are disabled because lab contact settings could not be loaded.';
  } else if (!hasAllocatedGlobalContact) {
    globalQuoteDisabledReason =
      'Quote requests are disabled until a lab contact is configured.';
  }

  const canRequestQuotes =
    !isViewer &&
    detail?.status === 'REQUESTED' && quoteLines.length > 0;

  const isQuoteInFlight = requestingQuoteScope != null;

  return (
    <>
      <Button variant="link" className="p-0" onClick={handleOpen}>
        {label}
      </Button>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        dialogClassName="procure-modal-glass procure-modal-glass-xl"
        aria-labelledby="order-request-detail-modal"
      >
        <Modal.Header closeButton className="procure-modal__header">
          <Modal.Title id="order-request-detail-modal">
            Order request {detail?.request_ref || label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="procure-modal__body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : detail ? (
            <>
              <p className="mb-2">
                <strong>Status:</strong> {detail.status}
                {detail.created_at && (
                  <>
                    {' '}
                    · <strong>Created:</strong>{' '}
                    {formatOrderContactUpdatedAt(detail.created_at) || '—'}
                  </>
                )}
              </p>
              {detail.status && detail.status !== 'REQUESTED' ? (
                <p className="mb-2">
                  <strong>
                    {detail.status === 'CANCELLED' ? 'Cancelled' : 'Fulfilled'}:
                  </strong>{' '}
                  {formatOrderContactUpdatedAt(
                    detail.fulfilled_at || detail.updated_at
                  ) || '—'}
                  {' '}
                  · <strong>Closed by:</strong>{' '}
                  {detail.fulfilled_by_name || '—'}
                </p>
              ) : null}
              <p className="mb-2">
                <strong>Requested by:</strong>{' '}
                {detail.requested_by_name || detail.requested_by || '—'}
                {detail.project_name && (
                  <>
                    {' '}
                    · <strong>Project:</strong> {detail.project_name}
                  </>
                )}
              </p>
              <p className="mb-2">
                <strong>Contact:</strong>{' '}
                {detail.contact_name || '—'}{' '}
                {detail.contact_email ? `<${detail.contact_email}>` : ''}
              </p>
              <OrderRequestLinesBySupplier
                supplierGroups={supplierGroups}
                canRequestQuotes={canRequestQuotes}
                globalQuoteDisabledReason={globalQuoteDisabledReason}
                requestingQuoteScope={requestingQuoteScope}
                quoteSuccessResult={quoteSuccessResult}
                onRequestQuotesForSupplier={supplierKey =>
                  handleRequestSupplierQuotes({ supplierKey })
                }
              />
              {quoteSuccessResult ? (
                <CatalogQuoteEmailResultSummary result={quoteSuccessResult} />
              ) : null}
              {quoteSubmitErrors.length > 0 ? (
                <Alert variant="danger" className="mb-3">
                  {quoteSubmitErrors.map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </Alert>
              ) : null}
              {canRequestQuotes && globalQuoteDisabledReason ? (
                <Alert variant="warning" className="mb-3">
                  {globalQuoteDisabledReason}{' '}
                  {!isLoadingGlobalContact && !globalContactLoadError ? (
                    <Button
                      variant="link"
                      className="p-0 align-baseline"
                      onClick={() => setShowGlobalContactModal(true)}
                    >
                      Set lab contact
                    </Button>
                  ) : null}
                </Alert>
              ) : null}
              <div className="mb-2">
                <label className="form-label">Notes</label>
                {isViewer || detail.status !== 'REQUESTED' ? (
                  <p className="mb-0">{notes || '—'}</p>
                ) : (
                <textarea
                  className="form-control"
                  rows={2}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Linked order id</label>
                {isViewer || detail.status !== 'REQUESTED' ? (
                  <p className="mb-0">{linkedOrder || '—'}</p>
                ) : (
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  value={linkedOrder}
                  onChange={e => setLinkedOrder(e.target.value)}
                  placeholder="Optional Materiah Order id"
                />
                )}
                {detail.linked_order != null && (
                  <div className="mt-2">
                    <OrderDetailModal orderId={detail.linked_order} />
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted">No details available.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="procure-modal__footer">
          {!isViewer && detail?.status === 'REQUESTED' && (
            <>
              <Button
                variant="success"
                disabled={pendingStatus != null || loading || isQuoteInFlight}
                onClick={() => handlePatch('FULFILLED')}
              >
                {pendingStatus === 'FULFILLED' ? 'Saving…' : 'Mark fulfilled'}
              </Button>
              <Button
                variant="outline-danger"
                disabled={pendingStatus != null || loading || isQuoteInFlight}
                onClick={() => handlePatch('CANCELLED')}
              >
                {pendingStatus === 'CANCELLED' ? 'Saving…' : 'Cancel request'}
              </Button>
            </>
          )}
          <Button
            variant="secondary"
            className="procure-modal__close-btn"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <GlobalOrderContactModal
        open={showGlobalContactModal}
        onClose={() => setShowGlobalContactModal(false)}
        token={token}
        onSettingsUpdated={settings => {
          setGlobalContactSettings(settings);
          setGlobalContactLoadError('');
          showToast('Lab contact saved successfully', 'success', 'top-right', 3000);
        }}
      />
    </>
  );
};

export default OrderRequestDetailModal;
